import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Plus, X, Loader2, ImageIcon } from "lucide-react";
import RichText from "./RichText";
import MediaPicker from "./MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const ax = axios.create({ baseURL: API, withCredentials: true });
const mediaSrc = (m) => {
  const url = typeof m === "string" ? m : m?.url;
  if (!url) return "";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const inputCls =
  "w-full rounded-xl border border-navy-100 bg-white px-4 py-3 text-sm text-navy-800 placeholder:text-navy-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20";

// ----------------------------------------------------------------------------
// Per-resource schema. Each field has: key, label, type, [optional helpers].
// Supported types: text, slug, textarea, rich, number, media_image, media_pdf,
// list_string, checkbox.
// ----------------------------------------------------------------------------
const SCHEMAS = {
  services: {
    label: "Service",
    keyField: "slug",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "slug", label: "Slug", type: "slug", required: true },
      { key: "division_slug", label: "Division", type: "select", options: ["medical-center", "medical-lab", "pharmacy-wellness"] },
      { key: "summary", label: "Summary (short)", type: "textarea" },
      { key: "description", label: "Full description", type: "rich" },
      { key: "duration", label: "Duration", type: "text" },
      { key: "price_note", label: "Price note", type: "text" },
      { key: "hero_media", label: "Hero image", type: "media_image" },
      { key: "includes", label: "What's included (one per line)", type: "list_string" },
    ],
  },
  projects: {
    label: "Project",
    keyField: "slug",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "slug", label: "Slug", type: "slug", required: true },
      { key: "sector", label: "Sector", type: "text" },
      { key: "client", label: "Client", type: "text" },
      { key: "year", label: "Year", type: "text" },
      { key: "summary", label: "Summary", type: "textarea" },
      { key: "body_html", label: "Body", type: "rich" },
      { key: "cover_media", label: "Cover image", type: "media_image" },
    ],
  },
  posts: {
    label: "Post",
    keyField: "slug",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "slug", label: "Slug", type: "slug", required: true },
      { key: "status", label: "Status", type: "select", options: ["draft", "published"] },
      { key: "excerpt", label: "Excerpt", type: "textarea" },
      { key: "body", label: "Body", type: "rich" },
      { key: "cover_media", label: "Cover image", type: "media_image" },
      { key: "published_at", label: "Published date", type: "text" },
    ],
  },
  faqs: {
    label: "FAQ",
    keyField: "id",
    fields: [
      { key: "question", label: "Question", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "order", label: "Order", type: "number" },
      { key: "answer", label: "Answer", type: "rich" },
    ],
  },
  divisions: {
    label: "Division",
    keyField: "slug",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "slug", label: "Slug", type: "slug", required: true },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "summary", label: "Summary", type: "textarea" },
      { key: "overview", label: "Overview", type: "rich" },
      { key: "extended_body", label: "Extended body", type: "rich" },
      { key: "hero_media", label: "Hero image", type: "media_image" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  testimonials: {
    label: "Testimonial",
    keyField: "id",
    fields: [
      { key: "name", label: "Person name", type: "text", required: true },
      { key: "title", label: "Title", type: "text" },
      { key: "organization", label: "Organization", type: "text" },
      { key: "quote", label: "Quote", type: "textarea" },
      { key: "photo_media", label: "Portrait", type: "media_image" },
    ],
  },
  partners: {
    label: "Partner",
    keyField: "id",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "logo_media", label: "Logo", type: "media_image" },
      { key: "url", label: "Website URL", type: "text" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  certifications: {
    label: "Certification",
    keyField: "id",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "issuer", label: "Issuer", type: "text" },
      { key: "year", label: "Year", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "logo_media", label: "Logo", type: "media_image" },
    ],
  },
  industries: {
    label: "Industry",
    keyField: "slug",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "slug", label: "Slug", type: "slug" },
      { key: "summary", label: "Summary", type: "textarea" },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  "why-cards": {
    label: "Why-choose card",
    keyField: "id",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "body", label: "Body", type: "textarea" },
      { key: "icon", label: "Icon name (lucide)", type: "text" },
    ],
  },
  "team-members": {
    label: "Team member",
    keyField: "slug",
    fields: [
      { key: "name", label: "Full name", type: "text", required: true },
      { key: "slug", label: "Slug (URL)", type: "slug", required: true },
      { key: "role", label: "Role / title", type: "text" },
      { key: "specialty", label: "Specialty (eyebrow)", type: "text" },
      { key: "qualifications", label: "Qualifications", type: "text" },
      { key: "division_slug", label: "Division", type: "select", options: ["medical-center", "medical-lab", "pharmacy-wellness"] },
      { key: "status", label: "Status (defaults to Published)", type: "select", options: ["published", "draft"] },
      { key: "email", label: "Email", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "photo_media", label: "Photo", type: "media_image" },
      { key: "bio", label: "Biography", type: "rich" },
      { key: "order", label: "Display order", type: "number" },
    ],
  },
  stats: {
    label: "Stat",
    keyField: "id",
    fields: [
      { key: "label", label: "Label", type: "text", required: true },
      { key: "value", label: "Value", type: "text" },
      { key: "suffix", label: "Suffix", type: "text" },
    ],
  },
};

// ----------------------------------------------------------------------------
function MediaField({ value, onChange, kind = "image", testId }) {
  const [open, setOpen] = useState(false);
  const src = mediaSrc(value);
  return (
    <div className="space-y-3" data-testid={testId}>
      {value && src ? (
        <div className="flex items-center gap-4">
          {kind === "image" ? (
            <img src={src} alt={value?.alt || ""} className="h-24 w-24 object-cover rounded-xl border border-navy-100 bg-soft-gray" />
          ) : (
            <div className="h-24 w-24 rounded-xl border border-navy-100 bg-soft-gray flex items-center justify-center text-navy-500 text-xs">PDF</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-navy-800 truncate">{value?.filename || value?.url}</p>
            <p className="text-[11px] text-navy-500 truncate">{value?.alt || "(no alt text)"}</p>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => setOpen(true)} className="text-xs font-semibold text-teal hover:underline" data-testid={`${testId}-change`}>Change</button>
              <button type="button" onClick={() => onChange(null)} className="text-xs font-semibold text-navy-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`${testId}-clear`}>
                <X className="h-3 w-3" /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`${testId}-open`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-navy-200 text-navy-700 text-sm font-semibold hover:border-teal hover:text-teal transition-colors"
        >
          <ImageIcon className="h-4 w-4" /> Choose from media library
        </button>
      )}
      {open && (
        <MediaPicker
          kind={kind}
          onClose={() => setOpen(false)}
          onSelect={(m) => { onChange(m); setOpen(false); }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
export default function ResourceEditor({ resource: resourceProp }) {
  const params = useParams();
  const resource = resourceProp || params.resource;
  const key = params.key;
  const nav = useNavigate();
  const isNew = key === "new";
  const schema = SCHEMAS[resource];
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!schema) return;
    if (isNew) {
      const blank = {};
      schema.fields.forEach((f) => {
        if (f.type === "list_string") blank[f.key] = [];
        else if (f.type === "checkbox") blank[f.key] = false;
        else if (f.type === "media_image" || f.type === "media_pdf") blank[f.key] = null;
        else blank[f.key] = "";
      });
      setDoc(blank);
    } else {
      ax.get(`/_crud/${resource}/${key}`)
        .then(({ data }) => setDoc(data))
        .catch(() => toast.error("Could not load record"));
    }
  }, [resource, key, isNew, schema]);

  if (!schema) return <div className="p-8 text-navy-700">Unknown resource.</div>;
  if (!doc) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;

  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      // Convert list_string textarea values back into clean arrays.
      const payload = { ...doc };
      schema.fields.forEach((f) => {
        if (f.type === "list_string" && typeof payload[f.key] === "string") {
          payload[f.key] = payload[f.key].split("\n").map((s) => s.trim()).filter(Boolean);
        }
        if (f.type === "number" && payload[f.key] !== "" && payload[f.key] !== null) {
          const n = Number(payload[f.key]);
          if (!Number.isNaN(n)) payload[f.key] = n;
        }
      });
      let saved;
      if (isNew) {
        const { data } = await ax.post(`/_crud/${resource}`, payload);
        saved = data;
      } else {
        const { data } = await ax.put(`/_crud/${resource}/${doc[schema.keyField] || doc.id}`, payload);
        saved = data;
      }
      toast.success("Saved");
      const newKey = saved?.[schema.keyField] || saved?.id;
      if (newKey && newKey !== key) {
        nav(`/admin/${resource}/${newKey}`, { replace: true });
      } else {
        setDoc(saved);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this record? This cannot be undone.")) return;
    try {
      await ax.delete(`/_crud/${resource}/${doc[schema.keyField] || doc.id}`);
      toast.success("Deleted");
      nav(`/admin/${resource}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-4xl">
      <Link to={`/admin/${resource}`} className="inline-flex items-center gap-1.5 text-sm text-navy-600 hover:text-teal" data-testid="resource-editor-back">
        <ArrowLeft className="h-4 w-4" /> Back to {schema.label} list
      </Link>
      <h1 className="mt-4 font-display font-bold text-3xl text-navy-800">
        {isNew ? `New ${schema.label}` : `Edit ${schema.label}`}
      </h1>

      <div className="mt-8 grid gap-5">
        {schema.fields.map((f) => {
          const v = doc[f.key];
          const id = `field-${f.key}`;
          if (f.type === "text" || f.type === "slug") {
            return (
              <label key={f.key} className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{f.label}{f.required && " *"}</span>
                <input
                  value={v ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={inputCls}
                  data-testid={id}
                />
              </label>
            );
          }
          if (f.type === "number") {
            return (
              <label key={f.key} className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{f.label}</span>
                <input
                  type="number"
                  value={v ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={inputCls}
                  data-testid={id}
                />
              </label>
            );
          }
          if (f.type === "textarea") {
            return (
              <label key={f.key} className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{f.label}</span>
                <textarea
                  value={v ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  rows={4}
                  className={inputCls}
                  data-testid={id}
                />
              </label>
            );
          }
          if (f.type === "select") {
            return (
              <label key={f.key} className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{f.label}</span>
                <select
                  value={v ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={inputCls}
                  data-testid={id}
                >
                  <option value="">—</option>
                  {f.options.map((o) => (<option key={o} value={o}>{o}</option>))}
                </select>
              </label>
            );
          }
          if (f.type === "checkbox") {
            return (
              <label key={f.key} className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!v}
                  onChange={(e) => set(f.key, e.target.checked)}
                  className="h-4 w-4 accent-teal"
                  data-testid={id}
                />
                <span className="text-sm text-navy-800">{f.label}</span>
              </label>
            );
          }
          if (f.type === "list_string") {
            const text = Array.isArray(v) ? v.join("\n") : v ?? "";
            return (
              <label key={f.key} className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{f.label}</span>
                <textarea
                  value={text}
                  onChange={(e) => set(f.key, e.target.value)}
                  rows={5}
                  className={inputCls}
                  data-testid={id}
                />
              </label>
            );
          }
          if (f.type === "rich") {
            return (
              <div key={f.key}>
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{f.label}</span>
                <RichText
                  value={v || ""}
                  onChange={(html) => set(f.key, html)}
                  placeholder={`Write ${f.label.toLowerCase()}…`}
                  testId={`richtext-${f.key}`}
                />
              </div>
            );
          }
          if (f.type === "media_image" || f.type === "media_pdf") {
            return (
              <div key={f.key}>
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{f.label}</span>
                <MediaField
                  value={v}
                  onChange={(m) => set(f.key, m)}
                  kind={f.type === "media_pdf" ? "pdf" : "image"}
                  testId={`media-${f.key}`}
                />
              </div>
            );
          }
          return null;
        })}
      </div>

      <div className="mt-10 flex items-center justify-between gap-4">
        {!isNew && (
          <button
            onClick={remove}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-navy-200 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors"
            data-testid="resource-editor-delete"
          >
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <Link to={`/admin/${resource}`} className="px-5 py-2.5 text-sm font-semibold text-navy-700 hover:text-teal">Cancel</Link>
          <button
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors disabled:opacity-60"
            data-testid="resource-editor-save"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

export function ResourceListWithEdit({ resource, label }) {
  const [list, setList] = useState([]);
  const schema = SCHEMAS[resource];
  useEffect(() => { ax.get(`/_crud/${resource}`).then(({ data }) => setList(data || [])); }, [resource]);
  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl text-navy-800">{label}</h1>
          <p className="mt-1 text-sm text-navy-700/70">{list.length} items.</p>
        </div>
        <Link
          to={`/admin/${resource}/new`}
          data-testid={`resource-new-${resource}`}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors"
        >
          <Plus className="h-4 w-4" /> New {schema?.label || "item"}
        </Link>
      </div>
      <div className="mt-6 rounded-2xl bg-white border border-navy-50 divide-y divide-navy-50">
        {list.length === 0 ? (
          <p className="p-10 text-center text-navy-500 text-sm">No records yet.</p>
        ) : list.map((i) => {
          const k = i[schema?.keyField || "id"] || i.id;
          return (
            <Link
              key={k}
              to={`/admin/${resource}/${k}`}
              data-testid={`resource-row-${k}`}
              className="p-5 flex items-center justify-between hover:bg-soft-gray transition-colors"
            >
              <div className="min-w-0">
                <p className="font-semibold text-navy-800 truncate">{i.title || i.name || i.question || i.label || i.slug}</p>
                <p className="text-xs text-navy-500 truncate">{i.slug || i.id}</p>
              </div>
              <span className="text-xs text-navy-400 shrink-0 ml-3">{(i.updated_at || i.created_at || "").slice(0, 10)}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
